import { fixText } from "../../../services/api"
import { useState, memo } from "react";
import { useToast } from "../../../contexts/ToastContext";
import { HoverToolTip } from '../Items/HoverToolTip';
import { MagicWand02Icon } from "hugeicons-react";
import { useSettings } from "../../../contexts/SettingsContext";
import { useAuth } from "../../../contexts/AuthContext";
import "../../../Styles/Layout/Buttons/PromptFixButton.css";

interface PromptFixButtonProps {
    input: string;
    setInput: (input: string) => void;
    isEditing?: boolean;
}

export const PromptFixButton = memo(({input, setInput, isEditing}: PromptFixButtonProps) => {
    const { enhancedPromptFixing} = useSettings()
    const [isFixing, setIsFixing] = useState(false);
    const toast = useToast();
    const { isAuthenticated } = useAuth();

    // /text/fix requires a signed-in user
    const shouldShow = isAuthenticated && input.length > 10 && input.length < 2000;

    const MIN_FIXING_VISIBLE_MS = 450;

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (isFixing) return;
        const inputArea = e.currentTarget.closest('form')?.querySelector<HTMLTextAreaElement>('textarea');

        if(input.length < 10){
            toast("Input too short", "error");
            return;
        }
        
        if(input.length > 2000){
            toast("Your Message is Too Long", "error");
            return;
        }
        
        const started = Date.now();
        try {
            setIsFixing(true);
            const response = await fixText(input, enhancedPromptFixing);
            setInput(response);
        } catch (error) {
            console.error('Error fixing prompt:', error);
            toast("Failed to enhance prompt", "error");
        } finally {
            const remaining = MIN_FIXING_VISIBLE_MS - (Date.now() - started);
            if (remaining > 0) {
                await new Promise((r) => setTimeout(r, remaining));
            }
            setIsFixing(false);
            inputArea?.focus();
        }
    }

    if(!shouldShow) return null;

    return (
        <button 
            type="button"
            className={`prompt-fix-button ${isFixing ? 'fixing' : ''}`} 
            onClick={handleClick}
            disabled={isFixing}
            aria-busy={isFixing}
            aria-label="Fix your prompt"
        >
            <MagicWand02Icon 
                aria-hidden="true"
                size={16}
            />
            <HoverToolTip text={isFixing ? "Enhancing" : "Fix your prompt"} position={isEditing ? 'top' : 'bottom'} />
        </button>
    )
});

PromptFixButton.displayName = 'PromptFixButton';
